import { useMemo, useRef, useState } from 'react';
import { PageHead } from '@components/ui/PageBits';
import Button from '@components/ui/Button';
import styles from './DocumentsPanel.module.css';

/* Shared KYC documents panel — used by both the doctor and clinic-admin portals.
   The caller passes its own status query + submit mutation (doctor or clinic
   endpoints); the rendering is identical. Onboarding docs unlock the 1-month
   trial; Final docs are due before it ends. A value field flagged `withFile`
   also accepts a supporting PDF / scan alongside the typed value. */

const PHASES = [
  { key: 'onboarding', title: 'Onboarding — to get started', sub: 'Submit these to start your 1-month trial.' },
  { key: 'final',      title: 'Final — within your 1-month trial', sub: 'Please submit before your trial ends.' },
];
const LEVEL_LABEL = { graduation: 'Graduation', post_graduation: 'Post Graduation', phd: 'Ph.D' };

/* Upload size limits — upper mirrors the server (UploadController: 8 MB);
   lower guards against blank / corrupt scans. */
const MAX_BYTES = 8 * 1024 * 1024;
const MIN_BYTES = 10 * 1024;
const ACCEPT_NOTE = 'PDF, JPG, PNG or WEBP · 10 KB to 8 MB';

/* Per-document guidance, keyed by docType. Helps when the obvious paper
   doesn't exist (e.g. a rented clinic whose bills carry the owner's name). */
const DOC_HINTS = {
  address_proof: (
    <>Any one showing the clinic address — electricity bill, gas (MGL) bill, property-tax receipt, or a registered rent / leave-&-licence agreement. <strong>Rented clinic?</strong> If no bill is in the clinic's or doctor's name, upload the rent agreement plus the owner's latest utility bill — any name on it is fine.</>
  ),
  cancelled_cheque: 'One cancelled cheque or passbook page — this single document proves both your bank account number and IFSC above (it shows the account number, IFSC and account-holder name).',
  profile_photo: 'A clear, recent passport-style photo on a plain background.',
  agreement: 'The signed service agreement provided by 1Ayurved.',
};

/* Local thumbnail of the just-picked / attached file. */
function DocThumb({ dataUrl, isPdf, name }) {
  return (
    <div className={styles.thumb}>
      {isPdf
        ? <div className={styles.thumbPdf}><i className="bi bi-filetype-pdf" aria-hidden="true" /></div>
        : <img className={styles.thumbImg} src={dataUrl} alt={name || 'Selected file'} />}
      <div className={styles.thumbMeta}>
        <span className={styles.thumbName}>{name || 'Selected file'}</span>
        <span className={styles.thumbTag}><i className="bi bi-check-circle" aria-hidden="true" /> Ready</span>
      </div>
    </div>
  );
}

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('Could not read the file.'));
  r.readAsDataURL(file);
});

export default function DocumentsPanel({ query, submit }) {
  const { data, isLoading, isError, error } = query;
  const [busyKey, setBusyKey] = useState(null);
  const [err, setErr] = useState('');

  const submittedMap = useMemo(() => {
    const m = {};
    (data?.submitted || []).forEach((it) => {
      m[it.docType] = it;
      if (it.level) m[`${it.docType}:${it.level}`] = it;
    });
    return m;
  }, [data]);

  if (isLoading) return <div className={styles.page}><div className={`skeleton ${styles.skel}`} /></div>;
  if (isError) return <div className={styles.page}><PageHead title="Documents unavailable" subtitle={error?.message} /></div>;

  const checklist = data?.checklist || {};
  const daysLeft = data?.daysLeft;
  const trialEnds = data?.trialEndsAt
    ? new Date(data.trialEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const doSubmit = async (key, payload) => {
    setErr('');
    setBusyKey(key);
    try {
      await submit.mutateAsync(payload);
    } catch (e) {
      setErr(e?.message || 'Could not submit.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className={styles.page}>
      <PageHead title="My documents" subtitle="Submit your onboarding and final documents. Files stay private." />

      {data?.trialEndsAt && (
        <div className={`${styles.trial} ${daysLeft <= 2 ? styles.trialUrgent : ''}`}>
          {data.progress?.final?.allSubmitted
            ? <><i className="bi bi-check-lg" aria-hidden="true" /> All documents submitted — thank you.</>
            : `Final documents due by ${trialEnds} · ${daysLeft} day(s) left.`}
        </div>
      )}

      {PHASES.map((ph) => {
        const items = checklist[ph.key] || [];
        const prog = data?.progress?.[ph.key];
        return (
          <div key={ph.key} className={styles.phase}>
            <div className={styles.phaseHead}>
              <div>
                <div className={styles.phaseTitle}>{ph.title}</div>
                <div className={styles.phaseSub}>{ph.sub}</div>
              </div>
              {prog && <span className={styles.phaseProg}>{prog.submitted}/{prog.required} submitted</span>}
            </div>

            <div className={styles.items}>
              {items.map((item) => (
                <DocItem
                  key={item.type}
                  item={item}
                  phase={ph.key}
                  submittedMap={submittedMap}
                  busyKey={busyKey}
                  onSubmit={doSubmit}
                  setErr={setErr}
                />
              ))}
            </div>
          </div>
        );
      })}

      {err && <div className={styles.err}>{err}</div>}
    </div>
  );
}

function StatusPill({ it }) {
  if (!it) return <span className={`${styles.pill} ${styles.pillPending}`}>Pending</span>;
  const s = it.status || 'submitted';
  if (s === 'verified') return <span className={`${styles.pill} ${styles.pillOk}`}><i className="bi bi-check-lg" aria-hidden="true" /> Verified</span>;
  if (s === 'rejected') return <span className={`${styles.pill} ${styles.pillRej}`}>Rejected</span>;
  return <span className={`${styles.pill} ${styles.pillSub}`}><i className="bi bi-check-lg" aria-hidden="true" /> Submitted</span>;
}

function DocItem({ item, phase, submittedMap, busyKey, onSubmit, setErr }) {
  const uploadRef = useRef(null);
  const attachRef = useRef(null);
  const [value, setValue] = useState('');
  const [attached, setAttached] = useState(null); // { name, dataUrl }
  const [preview, setPreview] = useState(null);   // { dataUrl, isPdf, name } — local thumbnail
  const [level, setLevel] = useState(item.levels?.[0] || null);

  const lookupKey = level ? `${item.type}:${level}` : item.type;
  const submitted = submittedMap[lookupKey];
  const busy = busyKey === lookupKey;
  const base = { docType: item.type, phase, level: level || undefined };

  // Reject oversized files up front so a slow upload doesn't fail at the server.
  const tooBig = (file) => {
    if (file.size < MIN_BYTES) { setErr?.('File looks too small (under 10 KB) — please upload a clear scan.'); return true; }
    if (file.size > MAX_BYTES) { setErr?.('File must be under 8 MB — please upload a smaller one.'); return true; }
    return false;
  };

  const doUpload = async (file) => {
    if (tooBig(file)) return;
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPreview({ dataUrl, isPdf: file.type === 'application/pdf', name: file.name });
      onSubmit(lookupKey, { ...base, data: dataUrl });
    } catch (e) { setErr?.(e?.message || 'Could not read the file.'); }
  };
  const pickAttach = async (file) => {
    if (tooBig(file)) return;
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setAttached({ name: file.name, dataUrl });
      setPreview({ dataUrl, isPdf: file.type === 'application/pdf', name: file.name });
    } catch (e) { setErr?.(e?.message || 'Could not read the file.'); }
  };
  const saveValue = () => {
    const payload = { ...base, value: value.trim() };
    if (attached?.dataUrl) payload.data = attached.dataUrl;
    onSubmit(lookupKey, payload);
  };

  return (
    <div className={styles.item}>
      <div className={styles.itemTop}>
        <span className={styles.itemLabel}>
          {item.label}{item.optional ? <span className={styles.opt}> (optional)</span> : null}
        </span>
        <StatusPill it={submitted} />
      </div>

      {DOC_HINTS[item.type] && <div className={styles.hint}>{DOC_HINTS[item.type]}</div>}

      {item.levels && (
        <div className={styles.levels}>
          {item.levels.map((lv) => (
            <button
              key={lv}
              type="button"
              className={`${styles.levelBtn} ${level === lv ? styles.levelOn : ''} ${submittedMap[`${item.type}:${lv}`] ? styles.levelDone : ''}`}
              onClick={() => setLevel(lv)}
            >
              {LEVEL_LABEL[lv] || lv}{submittedMap[`${item.type}:${lv}`] ? <> <i className="bi bi-check-lg" aria-hidden="true" /></> : ''}
            </button>
          ))}
        </div>
      )}

      <div className={styles.itemAction}>
        {item.upload ? (
          <>
            <input
              ref={uploadRef}
              type="file"
              accept="application/pdf,image/*"
              className={styles.file}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }}
            />
            <Button variant="pu" size="sm" disabled={busy} onClick={() => uploadRef.current?.click()}>
              {busy ? 'Uploading…' : (submitted ? 'Replace file' : 'Upload')}
            </Button>
          </>
        ) : (
          <>
            <input
              className={styles.input}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={item.mask ? 'Enter number' : `Enter ${item.label.toLowerCase()}`}
            />
            {item.withFile && (
              <>
                <input
                  ref={attachRef}
                  type="file"
                  accept="application/pdf,image/*"
                  className={styles.file}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) pickAttach(f); e.target.value = ''; }}
                />
                <Button variant="se" size="sm" onClick={() => attachRef.current?.click()} title="Attach a supporting PDF / scan">
                  {attached ? <><i className="bi bi-paperclip" aria-hidden="true" /> {attached.name.length > 16 ? attached.name.slice(0, 14) + '…' : attached.name}</> : <><i className="bi bi-paperclip" aria-hidden="true" /> Attach PDF</>}
                </Button>
              </>
            )}
            <Button variant="pu" size="sm" disabled={busy || !value.trim()} onClick={saveValue}>
              {busy ? 'Saving…' : (submitted ? 'Update' : 'Save')}
            </Button>
          </>
        )}
      </div>

      {(item.upload || item.withFile) && <div className={styles.note}><i className="bi bi-info-circle" aria-hidden="true" /> {ACCEPT_NOTE}</div>}

      {preview && <DocThumb dataUrl={preview.dataUrl} isPdf={preview.isPdf} name={preview.name} />}

      {item.mask && submitted?.value && <div className={styles.masked}>Stored: {submitted.value}</div>}
      {!preview && submitted?.hasFile && <div className={styles.masked}><i className="bi bi-paperclip" aria-hidden="true" /> File attached</div>}
    </div>
  );
}
